import * as React from 'react';
import { useCallback, useState } from 'react';
import { isNull } from 'lodash';
import { AnomalyType, AnomalyTypePicker } from './anomalyTypePicker';
import { InfoPopover } from '../../../../components/tooltip/infoPopover';

import { useTranslation } from '@lib/useTypedTranslation';
import { InitialTableState, useTableReducer } from '../../../../components/data-table/lib';
import { SortDirection } from '../../../../components/data-table/types';
import { List, AdditionalItems } from '../../../../components/list/list';
import { Search } from '../../../../components/controls/search';
import { useUserSettingsContext } from '../../../../context/userSettings';
import { useWorldRequest } from '../../../../lib/useWorldRequest';
import { useWorldAction } from '../../../../lib/useWorldAction';
import { useWorldSettingsContext } from '../../../../context/worldSettings';
import { convertFilterToQuery } from '../../../../components/filters/filterLib';
import { FilterContext } from '@components/data-table/filters/context';
import { IFilterResult } from '@components/filters/filterLib';
import {
  IDeviceAnomalyData,
  IDeviceAnomaliesListData,
  getDeviceAnomaliesList,
  exportDeviceAnomaliesListAction
} from '../../../../services/core/deviceAnomaliesList';
import { useDeviceAnomaliesListColumns } from './useDeviceAnomaliesListColumns';
import { useDeviceAnomaliesFilterOptions } from './filters/useDeviceAnomaliesFilterOptions';
import useNormalizedHeaderButtons from '../../../../components/controls/normalizedHeaderButtons';
import styled from 'styled-components';
import './deviceAnomaliesList.css';

const tableName = 'deviceAnomaliesList';
const defaultPageSize = 20;

// Default sort for the list. The anomaly type picker resets to this whenever the type changes,
// so field/direction are shared to keep the reset provably in step with the initial sort.
const initialTableSortField: string = 'score';
const initialTableSortColumn: number = 3;
const initialTableSortDirection: SortDirection = 'desc';
const initialSort = { column: initialTableSortColumn, direction: initialTableSortDirection, field: initialTableSortField };

const InfoIcon = styled.i`
  color: #7490b9;
  margin-right: 5px;
`;

const Info = styled.div`
  margin: 1.5rem 2.5rem 0.75rem;
  font-weight: 600;
`;

export const getDeviceAnomaliesColumnFromFilterGroup = (filterGroup: string): string => {
  const baseGroup = filterGroup.endsWith('_negate') ? filterGroup.slice(0, -'_negate'.length) : filterGroup;
  switch (baseGroup) {
  case 'manufacturers':
    return 'manufacturer';
  case 'models':
    return 'model';
  case 'groups':
    return 'group';
  case 'homeLocations':
  case 'homeLocation':
    return 'homeLocation';
  case 'platforms':
    return 'platformType';
  default:
    throw new Error('Unknown filter group: ' + filterGroup);
  }
};

// Search + export are grouped into a single header item so they break onto their own
// line together (as a pair) rather than wrapping individually.
function DeviceAnomaliesHeaderActions(props: {
  dataId: string,
  searchPlaceholder: string,
  onSearch: (text: string) => void,
  exportButtons: AdditionalItems
}) {
  return (
    <div className='device-anomalies-list_actions'>
      <Search
        dataId={props.dataId}
        inputProps={{ placeholder: props.searchPlaceholder }}
        onClicked={props.onSearch}
      />
      {props.exportButtons.map((button, index) => (
        <button.Component key={`actions-button-${index}`} {...button.props} />
      ))}
    </div>
  );
}

export function DeviceAnomaliesList() {
  const { tablePageSizes } = useUserSettingsContext();
  const { t } = useTranslation(['translation', 'anomalyDetectionBeta', 'anomaly']);
  const { smartGroupingHour, smartGroupingTimeZone } = useWorldSettingsContext().worldSettings;
  const anomaliesEnabled = !isNull(smartGroupingHour);
  const timeZone = smartGroupingTimeZone || 'UTC';

  const initialTableState: InitialTableState = {
    initialRows: 4,
    limit: tablePageSizes?.[tableName] || defaultPageSize,
    sort: initialSort,
    filter: {}
  };

  const [tableReducerProperties, tableReducerFunctions] = useTableReducer<IDeviceAnomalyData>(tableName, initialTableState);
  const [
    { offset, limit, sort, search, filter, data, isLoading, dataRequestId },
    { onFilter, onDataLoaded, onSearch }
  ] = [tableReducerProperties, tableReducerFunctions];

  const [platforms, setPlatforms] = useState<string[]>(['android', 'windows']);
  const [selectedAnomalyType, setSelectedAnomalyType] = useState<AnomalyType | null>(null);

  const handleListFetched = useCallback((result: IDeviceAnomaliesListData) => {
    onDataLoaded(result.list, result.totalCount, dataRequestId);
    if (result.platforms) {
      setPlatforms(result.platforms);
    }
  }, [onDataLoaded, dataRequestId]);

  const hasWindows = platforms.includes('windows');
  const hasAndroid = platforms.includes('android');

  const columns = useDeviceAnomaliesListColumns({ hasWindows, hasAndroid, selectedAnomalyType });

  const handleAnomalyTypeChange = useCallback((type: AnomalyType | null) => {
    setSelectedAnomalyType(type);
    // Reset to the default sort (score, descending). The score column's index shifts with the
    // selected type, so look it up dynamically; onSetSort (rather than onSort) keeps the
    // direction fixed regardless of the previously sorted column.
    const scoreColumnIndex = columns.findIndex(c => c.id === initialTableSortField);
    tableReducerFunctions.onSetSort(scoreColumnIndex, initialTableSortDirection, columns[scoreColumnIndex]);
  }, [tableReducerFunctions, columns]);

  const listFetcher = useCallback(() => {
    return getDeviceAnomaliesList({
      limit,
      offset,
      sort: { field: sort.field, order: sort.direction },
      search,
      filter: {
        ...convertFilterToQuery(filter),
        ...(selectedAnomalyType && { anomalyType: selectedAnomalyType })
      }
    });
  }, [offset, limit, sort, search, filter, selectedAnomalyType]);

  const exportDeviceAnomalies = useWorldAction(exportDeviceAnomaliesListAction);
  const onExportClicked = async (): Promise<string> => {
    return exportDeviceAnomalies({
      limit: 10000,
      offset: 0,
      sort: { field: sort.field, order: sort.direction },
      search,
      timeZone,
      filter: {
        ...convertFilterToQuery(filter),
        ...(selectedAnomalyType && { anomalyType: selectedAnomalyType })
      }
    });
  };

  const titleWithTooltip = (
    <>
      {t('ANOMALIES_BETA', { ns: 'anomalyDetectionBeta' })}
      <InfoPopover
        labelText='Anomalies Beta tooltip'
        placement='right'
        popoverPortalSelector='.device-anomalies-list'
      >
        {t('ANOMALIES_BETA_TOOLTIP', { ns: 'anomalyDetectionBeta' })}
      </InfoPopover>
    </>
  );

  const translationItems = {
    statusText: {
      key: 'DEVICE_ANOMALIES_RESULT',
      ns: 'anomalyDetectionBeta'
    },
    tableTitle: {
      text: titleWithTooltip
    }
  };

  const additionalHeaderButtons = useNormalizedHeaderButtons({
    export: {
      exportFunction: onExportClicked,
      filename: 'DeviceAnomalies.csv',
      disabled: isLoading || !data?.length
    }
  });

  const { availableFilters, filtersLoaded } = useDeviceAnomaliesFilterOptions();

  const handleFiltersUpdated = (newFilter: IFilterResult) => {
    onFilter(newFilter);
  };
  const handleCancel = (): void => { /* no-op */ };

  return (
    <FilterContext.Provider value={{
      getColumnFromFilterGroup: getDeviceAnomaliesColumnFromFilterGroup,
      filters: filter,
      filterOptions: { availableFilters, filtersLoaded },
      handleFiltersUpdated,
      handleCancel
    }}>
      <div className='device-anomalies-list'>
        {!anomaliesEnabled && (
          <Info>
            <InfoIcon className='fa fa-info-circle' />
            {t('ANOMALY_DETECTION_NOT_SUPPORTED_disabled', { ns: 'anomaly' })}
          </Info>
        )}
        <List<IDeviceAnomalyData, IDeviceAnomaliesListData, void, void, 'id'>
          dataId='device-anomalies-beta-list'
          dataTableEnabled={true}
          customHeader={false}
          hasCheckboxes={false}
          hasFilters={true}
          hasSearch={false}
          translationItems={translationItems}
          columns={columns}
          fetcher={listFetcher}
          onListFetched={handleListFetched}
          tableReducerFunctions={tableReducerFunctions}
          tableReducerProperties={tableReducerProperties}
          useRequestHook={useWorldRequest}
          options={{ rowIdDataPath: 'id' }}
          additionalHeaderItems={[
            {
              Component: AnomalyTypePicker,
              props: { selectedAnomalyType, onChange: handleAnomalyTypeChange, hasWindows, hasAndroid }
            },
            {
              Component: DeviceAnomaliesHeaderActions,
              props: {
                dataId: 'device-anomalies-beta-list',
                searchPlaceholder: t('SEARCH', { ns: 'translation' }),
                onSearch,
                exportButtons: additionalHeaderButtons
              }
            }
          ]}
          stickyColumns={{ '0': 1, '1200px': 4 }}
        />
      </div>
    </FilterContext.Provider>
  );
}
